import { useMemo } from 'react';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import {
  getAllCompositeSegments,
  getClonedPlayerConfigFromClip,
  isClipAudiogram,
  isClipFromRecordingStudio,
  isLiveEventRemotionWithPreviewAndAlignedStream,
  isClipFromUploads
} from '@goldcast/player-config/src/utils/clipUtils';
import { COMPOSITION_DIMENSIONS, VIDEO_QUALITY } from '@goldcast/player-config/src/constants/Remotion.constants';
import { useCroppedThumbnail } from '@/hooks/useCroppedThumbnail';

export const useSceneThumbnail = ({ clip, startTimeInSeconds }: { clip: Clip; startTimeInSeconds: number }) => {
  const playerConfig = useMemo(() => {
    return getClonedPlayerConfigFromClip(clip);
  }, [clip]);

  const allCompositeSegments = useMemo(() => {
    return getAllCompositeSegments(clip);
  }, [clip]);

  const showSceneThumbnail = useMemo(() => {
    if (!playerConfig) {
      return false;
    }
    if (!allCompositeSegments || allCompositeSegments.length === 0) {
      return false;
    }
    if (isLiveEventRemotionWithPreviewAndAlignedStream(clip) && !isClipAudiogram(clip)) {
      // Live event speaker layouts should use scene based thumbnail
      return true;
    }
    if (isClipFromUploads(clip) && !isClipAudiogram(clip)) {
      return true;
    }
    if (!isClipFromRecordingStudio(clip)) {
      return false;
    }
    if (isClipAudiogram(clip)) {
      return false;
    }
    return true; // Recording studio non-audiogram
  }, [clip, playerConfig, allCompositeSegments]);

  const { compositionHeight, compositionWidth } = useMemo(() => {
    const videoSize = clip.asset_metadata.size;

    const videoQuality = VIDEO_QUALITY.FULL_HD;
    const compositionHeight = COMPOSITION_DIMENSIONS[videoSize][videoQuality].HEIGHT;
    const compositionWidth = COMPOSITION_DIMENSIONS[videoSize][videoQuality].WIDTH;

    return {
      compositionHeight,
      compositionWidth
    };
  }, [clip.asset_metadata.size]);

  const { croppedImageUrl } = useCroppedThumbnail({ clip, startTimeInSeconds, showSceneThumbnail });

  return {
    showSceneThumbnail,
    playerConfig,
    compositionHeight,
    compositionWidth,
    croppedImageUrl
  };
};
